<template>
  <div class="content-block">
    <table class="asd-table">
      <TableHeading :leadingCols="leadingCols"
                    :trailingCols="[null, null]"
                    :cols="projectPropertiesToShow"
                    :defaultSorter="defaultSorter"
                    :userSorter="userSorter"
                    :addButton="addButton"
                    :menuItems="[
                      {icon: 'fa-file-export', label: 'Exportovat jako CSV', action: exportTableData},
                    ]"
                    @sorter-changed="onSorterChanged"
      >
        <SavedFilters
          :userFilter="userFilter"
          savedFilterId="project-table"
          @filter-changed="onFilterChanged"
        ></SavedFilters>
      </TableHeading>
      <TableFilter :leadingCols="leadingCols"
                   :trailingCols="[null, null]"
                   :cols="projectPropertiesToShow"
                   :userFilter="userFilter"
                   @filter-changed="onFilterChanged"
      ></TableFilter>
      <tbody>
        <ProjectsRow v-for="project in pagedProjects"
                     :key="project.id"
                     :projectId="project.id"
                     :propsToShow="projectPropertiesToShow"
        ></ProjectsRow>
      </tbody>
    </table>
    <Paginator v-model="currentPage"
               :numPages="numPages"
               @input="onPageChanged"
    ></Paginator>
  </div>
</template>

<script>
import ProjectsRow from 'CORE/components/project/row'
import TableFilter from 'CORE/components/table-view/filter'
import TableHeading from 'CORE/components/table-view/heading'
import SavedFilters from 'CORE/components/table-view/saved-filters'
import Paginator from 'CORE/components/table-view/paginator'
import _ from 'lodash'
import { czSorter } from 'CORE/utils/sorters'
import { normalizeFts } from 'CORE/components/data-type/comparators'
import ExportTableMixin from 'CORE/mixins/export-table'

export default {
    name: 'ProjectTable',
    components: {
        ProjectsRow,
        TableFilter,
        TableHeading,
        SavedFilters,
        Paginator,
    },
    mixins: [
        ExportTableMixin,
    ],
    props: {
        rootFilter: {
            type: Object,
            required: false,
            default: _ => {},
        },
        fixedPagination: {
            type: Boolean,
            default: false,
        },
        projectStatus: {
            type: String,
            required: false,
            default: _ => '$all',
        },
        queryFilter: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        querySorter: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        queryPager: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        addButton: {
            type: Object,
            required: false,
            default: _ => null,
        },
    },
    data() {
        return {
            userFilter: {},
            userSorter: {},
            currentPage: 0,
        }
    },
    computed: {
        defaultSorter() {
            return _.get(this.$store.getters.currentUser, 'company.settings.gui.sorter.projects', { column: '_type', order: 'asc' })
        },
        leadingCols() {
            const cols = [null, { label: 'Type', key: '_type' }]
            if (this.showProjectColumn) cols.push({ label: 'Projekt', key: '_project' })
            return cols
        },
        filteredProjects() {
            const filter = _.merge(this.userFilter, this.rootFilter)
            let projects = []
            if (this.projectStatus === '$all') {
                projects = this.$store.getters.templates
            } else if (this.projectStatus === '$active') {
                projects = this.$store.getters.activeProjects
            } else if (this.projectStatus === '$inactive') {
                projects = this.$store.getters.inactiveProjects
            } else if (this.projectStatus === '$deleted') {
                projects = this.$store.getters.disabledProjects
            } else {
                projects = this.$store.getters.projectsByStateId(this.projectStatus)
            }
            if (_.size(filter) === 0) {
                return projects
            }
            return _.filter(projects, p => {
                let matches = 0
                _.each(filter, (filterValue, column) => {
                    if (column === '_type') {
                        const projectType = this.$store.getters.projectTypeById(_.get(p, 'projectType.id'))
                        const normalizedFilterValue = normalizeFts(filterValue)
                        const normalizedProjectName = normalizeFts(_.get(projectType, 'name'))
                        if (normalizedProjectName.search(normalizedFilterValue) >= 0) {
                            matches++
                        }
                    } else {
                        const normalizedFtsValue = normalizeFts(_.get(p, `propertyValues.${column}.$fts`, ''))
                        if (normalizedFtsValue.search(normalizeFts(filterValue)) >= 0) {
                            matches++
                        }
                    }
                })
                return matches === _.size(filter)
            })
        },
        sortedProjects() {
            const defaultColumn = 'system-name'
            let tasks = this.filteredProjects
            tasks = _.sortWith(tasks, (taskA, taskB) => {
                let sortingValueA; let sortingValueB
                if (_.get(this, 'userSorter.column') === '_projectType') {
                    sortingValueA = _.get(taskA, `project.id`)
                    sortingValueB = _.get(taskB, `project.id`)
                } else if (_.get(this, 'userSorter.column') === '_type') {
                    sortingValueA = _.get(this.$store.getters.projectTypeById(_.get(taskA, `projectType.id`)), 'name')
                    sortingValueB = _.get(this.$store.getters.projectTypeById(_.get(taskB, `projectType.id`)), 'name')
                } else {
                    sortingValueA = _.get(taskA, `propertyValues.${_.get(this, 'userSorter.column', defaultColumn)}.$srt`)
                    sortingValueB = _.get(taskB, `propertyValues.${_.get(this, 'userSorter.column', defaultColumn)}.$srt`)
                }
                const sorter = czSorter(_.get(this, 'userSorter.order', 'desc'))(sortingValueA, sortingValueB)
                return sorter
            })
            return tasks
        },
        pagedProjects() {
            const paging = _.get(this.$store.getters.settings, 'gui.projectsPaging', 20)
            const start = this.currentPage * paging
            const end = start + paging
            return _.slice(this.sortedProjects, start, end)
        },
        projectPropertiesToShow() {
            let propertyTypes = []
            _.forEach(_.map(this.pagedProjects, 'propertyValues'), obj => {
                propertyTypes = _.union(propertyTypes, _.keys(obj))
            })
            propertyTypes = _.filter(propertyTypes, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                if (_.includes(_.get(pt, 'settings.visible', []), 'projectsTable')) {
                    return p
                }
            })
            propertyTypes = _.union(propertyTypes, _.filter(_.keys(this.userFilter), k => !_.startsWith(k, '_')))
            propertyTypes = _.sortBy(propertyTypes, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                return _.parseInt(_.get(pt, 'settings.order', 0))
            })
            return _.defaults(propertyTypes, [])
        },
        projectPropertiesObjectsToShow() {
            const tp = {}
            if (this.projectPropertiesToShow) {
                for (const p of this.projectPropertiesToShow) {
                    _.set(tp, p, this.$store.getters.propertyTypeByKey(p))
                }
                return tp
            }
            return {}
        },
        numPages() {
            return Math.ceil(_.size(this.filteredProjects) / _.get(this.$store.getters.settings, 'gui.projectsPaging', 20))
        },
        exportEntities() {
            return this.pagedProjects
        },
        exportKeys() {
            return this.projectPropertiesToShow
        },
    },
    mounted() {
        this.userFilter = this.queryFilter
        if (_.isEmpty(this.querySorter)) {
            this.userSorter = this.defaultSorter
        } else {
            this.userSorter = this.querySorter
        }

        this.currentPage = _.get(this.queryPager, 'page', 0)
    },
    methods: {
        onFilterChanged(params) {
            this.currentPage = 0
            this.$set(this, 'userFilter', _.pickBy(params, f => _.size(f) > 0))
            this.$emit('filter-changed', this.userFilter)
        },
        onSorterChanged(params) {
            this.userSorter = params
            this.$emit('sorter-changed', this.userSorter)
        },
        onPageChanged(page) {
            this.$emit('pager-changed', { page, numPages: this.numPages })
        },
    },
}
</script>
